import { useQuery } from "@tanstack/react-query";
import { unifiedSports } from "@/lib/sports/unifiedSportsService";
import type { MatchInfo, MatchSnapshot } from "@/lib/matches";
import type { ProviderFixture } from "@/lib/sports/providers/types";

/**
 * REAL_FIXTURES_AS_HOME_MATCH_SOURCE
 *
 * Build the public Home carousel from real provider fixtures returned by
 * `unifiedSports.getFixtures()`. Synthetic/demo `MATCHES` are NOT used as
 * a public carousel source — they remain only for the pitch/radar preview.
 *
 * Mapping ProviderFixture → MatchInfo/MatchSnapshot:
 *   - id              → `real:${fixture.id}` (namespaced so it can never
 *                       collide with synthetic ids)
 *   - homeCode/awayCode → first 3 letters of team name, uppercased
 *   - homeFlag/awayFlag → "" (no FIFA mapping required for carousel display)
 *   - homeColor/awayColor → neutral defaults
 *   - startOffset     → 0 (no simulation clock for real fixtures)
 *   - providerFixtureId → numeric provider id if parseable
 *   - isSynthetic     → false
 *
 *   - snapshot.status  → ProviderFixture.status (SCHEDULED|LIVE|FINISHED→FT)
 *   - snapshot.minute  → 0 until a real live-details fetch supplies it
 *   - snapshot.kickoff → ISO kickoff string (rendered for SCHEDULED status)
 *
 * No H2H resolution is required: real fixtures are sourced directly from
 * the provider, not derived from synthetic pair lookups.
 */

function shortCode(name: string): string {
  const clean = (name ?? "").replace(/[^A-Za-z]/g, "");
  if (!clean) return "—";
  return clean.slice(0, 3).toUpperCase();
}

function fixtureToMatchInfo(f: ProviderFixture): MatchInfo {
  const numericId = Number(f.id);
  return {
    id: `real:${f.id}`,
    homeCode: shortCode(f.homeTeam),
    awayCode: shortCode(f.awayTeam),
    homeFlag: "",
    awayFlag: "",
    homeColor: "#3b82f6",
    awayColor: "#ef4444",
    startOffset: 0,
    providerFixtureId: Number.isFinite(numericId) ? numericId : undefined,
    isSynthetic: false,
  };
}

function fixtureToSnapshot(f: ProviderFixture): MatchSnapshot {
  const status: MatchSnapshot["status"] =
    f.status === "FINISHED" ? "FT" : f.status === "LIVE" ? "LIVE" : "SCHEDULED";
  return {
    id: `real:${f.id}`,
    homeScore: 0,
    awayScore: 0,
    minute: 0,
    status,
    kickoff: f.kickoff,
  };
}

export interface RealFixtureHub {
  loading: boolean;
  isDemoProvider: boolean;
  matches: MatchInfo[];
  snapshots: Record<string, MatchSnapshot>;
  liveId: string | null;
}

export function useRealFixtureMatches(): RealFixtureHub {
  const { data, isLoading } = useQuery({
    queryKey: ["real-fixture-matches"],
    queryFn: async () => {
      const isDemoProvider = unifiedSports.getActiveProvider() === "demo";
      if (isDemoProvider) {
        return { isDemoProvider: true, fixtures: [] as ProviderFixture[] };
      }
      const fixtures = await unifiedSports.getFixtures();
      return { isDemoProvider: false, fixtures };
    },
    staleTime: 60_000,
    refetchInterval: 60_000,
    retry: 1,
  });

  const fixtures = data?.fixtures ?? [];
  const upcomingOrLive = fixtures
    .filter((f) => f.status === "SCHEDULED" || f.status === "LIVE")
    .sort(
      (a, b) => new Date(a.kickoff).getTime() - new Date(b.kickoff).getTime(),
    )
    .slice(0, 8);

  const matches = upcomingOrLive.map(fixtureToMatchInfo);
  const snapshots: Record<string, MatchSnapshot> = {};
  upcomingOrLive.forEach((f) => {
    snapshots[`real:${f.id}`] = fixtureToSnapshot(f);
  });
  const liveFixture = upcomingOrLive.find((f) => f.status === "LIVE");
  const liveId = liveFixture ? `real:${liveFixture.id}` : null;

  return {
    loading: isLoading,
    isDemoProvider: data?.isDemoProvider ?? false,
    matches,
    snapshots,
    liveId,
  };
}